<template>
  <div>
    <router-view></router-view>

    <vue-toast style="z-index: 1000" ref="toast"/>

    <app-modal ref="modal"/>

    <portal-target name="popper-root"/>

    <app-loader ref="loader" class="loader-overlay--dark"/>
  </div>
</template>

<script>
  import Vue from 'vue'
  import vueToast from 'vue-toast'
  import { mapGetters, mapActions } from 'vuex'
  import AppModal from 'components/common/Modal'
  import AppLoader from 'components/common/Loader'
  import LocalStorage from 'modules/LocalStorage'
  import CheckUserRoleMixin from 'modules/mixins/CheckUserRole'
  import { apiGetUserInfo, apiLogout } from '@/api/user.api'
  import { errorMessage } from 'modules/Error'

  const bus = new Vue({})

  export default {
    name: 'app',

    mixins: [CheckUserRoleMixin],

    data () {
      return {
        bus: bus,
        token: null
      }
    },

    components: {
      'vue-toast': vueToast,
      'app-modal': AppModal,
      'app-loader': AppLoader
    },

    computed: {
      ...mapGetters({
        user: 'getUser'
      }),
      
      isDesigner () {
        return this.checkUserRole(this.user.role, ['designer'])
      }
    },

    methods: {
      ...mapActions([
        'updateUser',
        'initMessages'
      ]),
      /*
       * Init
       * It's initiated on lifecycle hook created() - when app is created
       * */
      init () {
        this.addInterceptor()
        this.bindEvents()

        this.getAuthToken()
      },

      /*
       * Bind Events
       * Global state functions or events
       * */
      bindEvents () {
        this.$bus.$on('modalShow', (component) => {
          this.$refs.modal.onShow(component)
        })

        this.$bus.$on('modalClose', () => {
          this.$refs.modal.onClose()
        })

        this.$bus.$on('loaderShow', () => {
          this.$refs.loader.onShow()
        })

        this.$bus.$on('loaderClose', () => {
          this.$refs.loader.onClose()
        })

        this.$bus.$on('toast', (msg) => {
          this.$refs.toast.showToast(msg, {
            theme: 'info',
            timeLife: 4000,
            closeBtn: false
          })
        })

        this.$bus.$on('toastSuccess', (msg) => {
          this.$refs.toast.showToast('<i class="checkmark icon"></i> ' + msg, {
            theme: 'success',
            timeLife: 4000,
            closeBtn: false
          })
        })

        this.$bus.$on('toastError', (msg) => {
          this.$refs.toast.showToast('<i class="remove icon"></i> ' + msg, {
            theme: 'error',
            timeLife: 4000,
            closeBtn: false
          })
        })

        this.$bus.$on('checkPageAccess', () => {
          this.checkPageAccess()
        })

        this.$bus.$on('checkAccessAndLogin', () => {
          this.checkAccessAndLogin()
        })

        this.$bus.$on('setAuthToken', (token) => {
          this.setAuthToken(token)
        })

        this.$bus.$on('logout', () => {
          this.onLogout()
        })

        this.$bus.$on('bodyOverflow', () => {
          this.toggleBodyOverflow()
        })

        this.$bus.$on('textareaAutoresize', (event) => {
          this.textareaAutoresize(event)
        })

        this.$bus.$on('enterShiftNewline', (event) => {
          this.enterShiftNewline(event)
        })
      },

      /*
       * Interceptor of HTTP requests
       * */
      addInterceptor () {
        Vue.http.interceptors.push((request, next) => {
          const isRegGetDribbbleShots = request.url.includes('https://api.dribbble.com')

          if (this.token && !isRegGetDribbbleShots) {
            request.headers.set('Auth-Token', this.token)
          }

          next((res) => {
            /*
             * Response status 401 - Not authorized
             * Logout user if he runs in the response with status 401 as he has authorization
             * */
            if (res.status === 401) {
              this.onLogout()
            }

            if (res.status === 412 || res.status === 500) {
              this.$bus.$emit('loaderClose')
            }
          })
        })
      },

      /*
       * Get Authorization token from local storage
       * */
      getAuthToken () {
        const token = LocalStorage.get('token')

        if (token) {
          this.setAuthToken(token)
        }
      },

      /*
       * Set Authorization token
       * @param {string} token A param with session auth token which is always needed for user
       * to be logged in and be able to obtain data
       * */
      setAuthToken (token) {
        this.token = token
        LocalStorage.set('token', token)
        this.getUserInfo()
      },

      /*
       * Get Current user data
       * Save it to shared storage so other components are able to get and watch user data as well
       * */
      async getUserInfo () {
        const [err, data] = await apiGetUserInfo()
        if (err) return errorMessage(err, 'Unable to access user info')

        this.updateUser(data)
        if (!this.$route.path.includes('/project/')) {
          this.initMessages()
        }
        if (this.isDesigner) {
          this.$store.dispatch('getWorkLog')
        }
      },

      /*
       * Check Page Access
       * If user has access(token) and is on auth route, redirect user to main route
       * */
      checkPageAccess () {
        const token = LocalStorage.get('token')

        if (token && this.$route.meta.checkAuth === true) {
          this.checkAccessAndLogin()
        }
      },

      async checkAccessAndLogin () {
        const [err, user] = await apiGetUserInfo()
        if (err) return errorMessage(err, 'Unable to access user info')

        this.updateUser(user)

        if (user.role === 'designer') return this.$router.push({name: 'schedule'})

        return ['admin', 'projectManager'].includes(user.role)
          ? this.$router.push({name: 'tasks'})
          : this.$router.push({name: 'projects'})    
      },

      /*
       * Logout
       * Removes the auth token from local storage, redirects to main app route and refresh the page
       * to reset all states within the app
       * */
      async onLogout () {
        const [err] = await apiLogout()
        if (err) return errorMessage(err, 'Unable to logout')

        LocalStorage.remove('token')
        this.$cookie.delete('PersistentCookie')
        window.top.location.href = '/'
        return window.location.reload()
      },

      /*
       * Body Overflow
       * Toggles body css property overflow: 'hidden' so page cannot be scrolled
       * */
      toggleBodyOverflow () {
        document.body.classList.toggle('overflow')
      },

      /*
       * Textarea Autoresize
       * Autorizes textarea based on content inside
       * */
      textareaAutoresize (event) {
        const el = event.target

        if (el.value) {
          el.style.height = '0px'
          el.style.height = el.scrollHeight + 'px'
        } else {
          el.style.height = ''
        }
      },

      /*
       * Enter + Shit is new line
       * Prevent actions with only enter, if shift pressed as well, go to new line
       * */
      enterShiftNewline (event) {
        if (event.keyCode === 13 && !event.shiftKey) {
          event.preventDefault()
        }
      }
    },

    /*
     * Lifecycle Hooks
     * */
    /* Created */
    created () {
      this.init()
    }
  }
</script>



// WEBPACK FOOTER //
// App.vue?a872607a